import Button from '@src/shared/ui/Button/Button';
import IconButton from '@src/shared/ui/Button/IconButton';
import { ArrowLeftIcon, ArrowRightIcon } from '@src/shared/ui/icons';
import { Box, Stack } from '@src/shared/ui/layout';
import Typography from '@src/shared/ui/Typography';
import { useTranslation } from 'react-i18next';
import styles from './styles';

export type YearViewProps = {
  startYear: number;
  currentYear: number;
  onPrev: () => void;
  onNext: () => void;
  onSelect: (year: number) => void;
  onBackToDay: () => void;
  minDate?: Date;
  maxDate?: Date;
};

function YearView({
  startYear,
  currentYear,
  onPrev,
  onNext,
  onSelect,
  onBackToDay,
  minDate,
  maxDate,
}: YearViewProps) {
  const { t } = useTranslation();
  const endYear = startYear + 14;
  const years = Array.from({ length: 15 }, (_, i) => startYear + i);
  const minYear = minDate?.getFullYear();
  const maxYear = maxDate?.getFullYear();
  const prevDisabled = minYear != null && startYear <= minYear;
  const nextDisabled = maxYear != null && endYear >= maxYear;

  return (
    <Box sx={styles.yearViewRoot}>
      <Stack direction="row" sx={styles.header}>
        <IconButton
          size="small"
          disabled={prevDisabled}
          onClick={onPrev}
          tooltipTitle={t('smart_date.previous_years', 'Previous years')}
        >
          <ArrowLeftIcon fontSize="small" />
        </IconButton>
        <Typography
          variant="body2"
          onClick={onBackToDay}
          sx={styles.yearHeaderLabel}
        >
          {startYear}–{endYear}
        </Typography>
        <IconButton
          size="small"
          disabled={nextDisabled}
          onClick={onNext}
          tooltipTitle={t('smart_date.next_years', 'Next years')}
        >
          <ArrowRightIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Box sx={styles.yearGrid}>
        {years.map((y) => {
          const isOutOfYearRange =
            (minYear != null && y < minYear) ||
            (maxYear != null && y > maxYear);
          const selected = y === currentYear;
          return (
            <Button
              key={y}
              variant="text"
              disabled={isOutOfYearRange}
              onClick={() => onSelect(y)}
              className={`year-button${selected ? ' selected' : ''}`}
              sx={[styles.yearButton, selected && styles.yearButtonSelected]}
            >
              {y}
            </Button>
          );
        })}
      </Box>
    </Box>
  );
}

export default YearView;
